import { Banner, Button, Select } from '@cloudflare/kumo';
import { useCallback, useEffect, useState } from 'react';
import { StatusIndicator } from '../../components/ui/StatusDot';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { usd } from './agentFormat';
import { SkeletonTable, SkeletonText } from '../../components/ui/Skeleton';

export type TurnStatus = 'ok' | 'error' | 'fallback';
export interface Turn {
  id: string; sessionId: string; status: TurnStatus; createdAt: string; ms: number; firstTokenMs: number; model: string;
  tokensIn: number; tokensOut: number; costMicroUsd: number | null; tools: number; toolErrors: number; input: string; error?: string; channel: string;
}
interface TurnDetail extends Omit<Turn, 'tools' | 'toolErrors'> {
  traceId: string | null; usedModel: string; version: number; page: string | null; output: string;
  tools: Array<{ name: string; ms: number; ok: boolean; error?: string }>;
}

const FILTERS = [
  { value: '', label: 'Todos os turnos' },
  { value: 'error', label: 'Com erro' },
  { value: 'fallback', label: 'Usaram o modelo de reserva' },
  { value: 'tool_error', label: 'Ferramenta falhou' },
];
const secs = (ms: number) => (ms ? `${(ms / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s` : '—');
const PAGE = 30;

export function Status({ s }: { s: TurnStatus }) {
  if (s === 'error') return <StatusIndicator type="error">Erro</StatusIndicator>;
  if (s === 'fallback') return <StatusIndicator type="warning">Reserva</StatusIndicator>;
  return <StatusIndicator type="success">Ok</StatusIndicator>;
}

export function Detail({ agentId, runId }: { agentId: string; runId: string }) {
  const fetchApi = useAuthenticatedFetch();
  const [d, setD] = useState<TurnDetail | null>(null);
  useEffect(() => {
    let alive = true;
    fetchApi(`/api/ai/agents/${agentId}/turns/${runId}`).then((r) => (r.ok ? r.json() : null)).then((j) => { if (alive && j?.turn) setD(j.turn); }).catch(() => undefined);
    return () => { alive = false; };
  }, [agentId, runId, fetchApi]);
  if (!d) return <SkeletonText lines={4} label="Carregando o trace" />;
  return (
    <div className="space-y-2 p-3 text-sm" data-testid="agent-turn-detail">
      <div><b>Visitante:</b> {d.input || '—'}</div>
      <div><b>Resposta:</b> {d.output || '—'}</div>
      {d.error && <Banner variant="error" description={<>{d.error}</>} />}
      <div className="grid gap-x-6 gap-y-1" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <span style={{ gridColumn: '1 / -1', wordBreak: 'break-word' }}>Modelo: {d.usedModel || d.model}{d.usedModel && d.usedModel !== d.model ? ` (principal: ${d.model})` : ''}</span>
        <span>Versão publicada: {d.version}</span>
        <span>1ª palavra: {secs(d.firstTokenMs)}</span>
        <span>Resposta completa: {secs(d.ms)}</span>
        <span>Tokens: {d.tokensIn.toLocaleString('pt-BR')} entrada · {d.tokensOut.toLocaleString('pt-BR')} saída</span>
        <span>Custo estimado: {usd(d.costMicroUsd)}</span>
        {d.page && <span>Página: {d.page}</span>}
        <span>Canal: {d.channel}</span>
      </div>
      {d.tools.length > 0 && (
        <ol className="list-decimal pl-5">
          {d.tools.map((t, i) => (
            <li key={i}>
              <code>{t.name}</code> · {t.ms} ms · {t.ok ? 'ok' : <span style={{ color: 'var(--danger, #d91515)' }}>falhou{t.error ? `: ${t.error}` : ''}</span>}
            </li>
          ))}
        </ol>
      )}
      {d.traceId && <div className="text-xs !text-[var(--text-muted)]" style={{ overflowWrap: 'anywhere' }}>trace {d.traceId} · procure por ele nos Logs do Worker e nos metadados do AI Gateway</div>}
    </div>
  );
}

/** Observabilidade: cada turno da versão no ar, com tempo, tokens, custo e ferramentas (filtra erro, reserva e falha de ferramenta) */
export function AgentTurnsPanel({ agentId }: { agentId: string }) {
  const fetchApi = useAuthenticatedFetch();
  const [filter, setFilter] = useState('');
  const [turns, setTurns] = useState<Turn[] | null>(null);
  const [more, setMore] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const fetchPage = useCallback(async (before?: string) => {
    const q = new URLSearchParams({ limit: String(PAGE) });
    if (filter) q.set('filter', filter);
    if (before) q.set('before', before);
    const r = await fetchApi(`/api/ai/agents/${agentId}/turns?${q}`);
    return r.ok ? ((await r.json()).turns as Turn[]) : [];
  }, [agentId, filter, fetchApi]);

  useEffect(() => {
    let alive = true;
    setTurns(null); setOpen(null);
    fetchPage().then((t) => { if (alive) { setTurns(t); setMore(t.length === PAGE); } }).catch(() => { if (alive) setTurns([]); });
    return () => { alive = false; };
  }, [fetchPage]);

  const loadMore = async () => {
    const last = turns?.[turns.length - 1];
    if (!last) return;
    const next = await fetchPage(last.createdAt);
    setTurns((cur) => [...(cur ?? []), ...next]);
    setMore(next.length === PAGE);
  };

  return (
    <section className="space-y-3" data-testid="ai-agent-turns">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-lg font-semibold" style={{ fontSize: 16 }}>Observabilidade</h3>
        <Select value={(FILTERS.find((f) => f.value === filter) ?? FILTERS[0])?.value ?? null} items={FILTERS} onValueChange={(value: string | null) => setFilter(value ?? '')} aria-label="Filtro" />
      </div>
      {!turns && <SkeletonTable rows={5} cols={4} label="Carregando turnos" />}
      {turns?.length === 0 && <div className="text-sm !text-[var(--text-muted)]">Nenhum turno registrado ainda. Os turnos aparecem quando a versão publicada responde pelo runtime.</div>}
      {turns && turns.length > 0 && (
        <ul className="m-0 list-none p-0">
          {turns.map((t) => (
            <li key={t.id} style={{ borderTop: '1px solid var(--border)' }}>
              <button
                type="button"
                onClick={() => setOpen(open === t.id ? null : t.id)}
                aria-expanded={open === t.id}
                className="block w-full cursor-pointer border-0 bg-transparent px-0 py-2 text-left text-sm"
                style={{ color: 'inherit', font: 'inherit' }}
                data-testid="agent-turn-row"
              >
                <div className="flex items-center justify-between gap-2">
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.input || '—'}</span>
                  <Status s={t.status} />
                </div>
                <div className="text-xs !text-[var(--text-muted)]">
                  {new Date(t.createdAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  {' · '}{secs(t.ms)}{' · '}{(t.tokensIn + t.tokensOut).toLocaleString('pt-BR')} tokens{' · '}{usd(t.costMicroUsd)}
                  {t.toolErrors > 0 ? ` · ${t.toolErrors} ferramenta(s) falhou` : ''}
                </div>
              </button>
              {open === t.id && <Detail agentId={agentId} runId={t.id} />}
            </li>
          ))}
        </ul>
      )}
      {more && <Button variant="secondary" onClick={loadMore}>Carregar mais</Button>}
    </section>
  );
}
